import { useState } from "react"
import {
  Briefcase,
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  MoreVertical,
  Network,
  Pencil,
  Trash2,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { OrgChartNode, Position } from "@/types"

/** sessionStorage-Key fuer den Auf-/Zuklapp-Zustand des Organigramm-Baums (pro Sitzung). */
const EXPAND_STORAGE_KEY = "org-chart-tree-expanded"

function loadExpandedState(): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(EXPAND_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveExpandedState(state: Record<string, boolean>) {
  try {
    sessionStorage.setItem(EXPAND_STORAGE_KEY, JSON.stringify(state))
  } catch {
    // sessionStorage nicht verfuegbar (privater Modus o.ae.) — Zustand bleibt In-Memory
  }
}

interface OrgChartTreeProps {
  roots: OrgChartNode[]
  onSelect: (node: OrgChartNode) => void
  selectedId?: string | null
  /** Alle Stellen — wird pro Knoten nach orgUnitId gefiltert (Anzeige als Blätter). */
  positions: Position[]
  onSelectPosition: (position: Position) => void
  canWrite: boolean
  /** Löschen erfordert zusaetzlich die "loeschen"-Berechtigung (Backend-Guard, siehe organization.py). */
  canDelete: boolean
  onCreateChild: (parent: { id: string; name: string }) => void
  /** "Stelle hinzufügen" am Knoten — öffnet das PositionSheet mit vorbelegter Einheit. */
  onAddPosition: (unit: { id: string; name: string }) => void
  onRename: (unit: { id: string; name: string }) => void
  onMove: (unit: { id: string; name: string }) => void
  onDelete: (unit: { id: string; name: string; hasChildren: boolean; positionCount: number }) => void
}

/** Rekursiver Organigramm-Baum: Org-Einheiten + darunter deren Stellen als Blätter,
 * Zaehler-Badge (Stellen direkt an der Einheit, siehe ANNAHME in OrganizationPage),
 * Auf-/Zuklappen mit sessionStorage-Persistenz, Pflege-Kontextmenü pro Knoten. */
export function OrgChartTree({
  roots,
  onSelect,
  selectedId,
  positions,
  onSelectPosition,
  canWrite,
  canDelete,
  onCreateChild,
  onAddPosition,
  onRename,
  onMove,
  onDelete,
}: OrgChartTreeProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpandedState)

  function toggle(id: string, defaultValue: boolean) {
    setExpanded((prev) => {
      const current = prev[id] ?? defaultValue
      const next = { ...prev, [id]: !current }
      saveExpandedState(next)
      return next
    })
  }

  return (
    <ul className="space-y-1">
      {roots.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          depth={0}
          onSelect={onSelect}
          selectedId={selectedId}
          positions={positions}
          onSelectPosition={onSelectPosition}
          canWrite={canWrite}
          canDelete={canDelete}
          onCreateChild={onCreateChild}
          onAddPosition={onAddPosition}
          onRename={onRename}
          onMove={onMove}
          onDelete={onDelete}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </ul>
  )
}

function TreeNode({
  node,
  depth,
  onSelect,
  selectedId,
  positions,
  onSelectPosition,
  canWrite,
  canDelete,
  onCreateChild,
  onAddPosition,
  onRename,
  onMove,
  onDelete,
  expanded,
  onToggle,
}: {
  node: OrgChartNode
  depth: number
  onSelect: (node: OrgChartNode) => void
  selectedId?: string | null
  positions: Position[]
  onSelectPosition: (position: Position) => void
  canWrite: boolean
  canDelete: boolean
  onCreateChild: (parent: { id: string; name: string }) => void
  /** "Stelle hinzufügen" am Knoten — öffnet das PositionSheet mit vorbelegter Einheit. */
  onAddPosition: (unit: { id: string; name: string }) => void
  onRename: (unit: { id: string; name: string }) => void
  onMove: (unit: { id: string; name: string }) => void
  onDelete: (unit: { id: string; name: string; hasChildren: boolean; positionCount: number }) => void
  expanded: Record<string, boolean>
  onToggle: (id: string, defaultValue: boolean) => void
}) {
  const nodePositions = positions.filter((p) => p.orgUnitId === node.id)
  const hasChildren = node.children.length > 0
  const hasContent = hasChildren || nodePositions.length > 0
  // Alle Ebenen initial aufgeklappt, damit Stellen auf tiefen Ebenen sofort
  // sichtbar sind (Gros der Stellen sitzt auf Ebene 3-5); danach Session-Zustand massgeblich
  const defaultValue = true
  const isExpanded = expanded[node.id] ?? defaultValue

  const isEmptyNode = !hasChildren && nodePositions.length === 0
  const canDeleteThisNode = canDelete && isEmptyNode

  return (
    <li>
      <div
        className={cn(
          "group flex items-center gap-1 rounded-md",
          selectedId === node.id && "bg-accent"
        )}
        style={{ marginLeft: depth * 20 }}
      >
        {hasContent ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-6 shrink-0"
            aria-label={isExpanded ? "Zuklappen" : "Aufklappen"}
            onClick={() => onToggle(node.id, defaultValue)}
          >
            {isExpanded ? (
              <ChevronDown className="!size-4" />
            ) : (
              <ChevronRight className="!size-4" />
            )}
          </Button>
        ) : (
          <span className="inline-block size-6 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node)}
          className="flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Network className="size-4 shrink-0 text-muted-foreground" />
          <span className="font-medium">{node.name}</span>
          <span className="text-xs text-muted-foreground">Ebene {node.level}</span>
          {nodePositions.length > 0 && (
            <Badge variant="secondary" className="ml-1">
              {nodePositions.length} {nodePositions.length === 1 ? "Stelle" : "Stellen"}
            </Badge>
          )}
        </button>
        {canWrite && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                aria-label={`Aktionen für ${node.name}`}
              >
                <MoreVertical className="!size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onCreateChild({ id: node.id, name: node.name })}>
                <FolderPlus className="mr-2 size-4" />
                Untereinheit anlegen
              </DropdownMenuItem>
              {/* Geschäftsführung = Stelle an der obersten Einheit — kein Sonder-Flag,
                  daher identische Aktion an jedem Knoten (inkl. Wurzel). */}
              <DropdownMenuItem onClick={() => onAddPosition({ id: node.id, name: node.name })}>
                <BriefcaseBusiness className="mr-2 size-4" />
                Stelle hinzufügen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRename({ id: node.id, name: node.name })}>
                <Pencil className="mr-2 size-4" />
                Umbenennen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMove({ id: node.id, name: node.name })}>
                <Network className="mr-2 size-4" />
                Verschieben
              </DropdownMenuItem>
              {canDeleteThisNode ? (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() =>
                    onDelete({
                      id: node.id,
                      name: node.name,
                      hasChildren,
                      positionCount: nodePositions.length,
                    })
                  }
                >
                  <Trash2 className="mr-2 size-4" />
                  Löschen
                </DropdownMenuItem>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Radix-DropdownMenuItem verweigert disabled+Tooltip nicht — span-Wrapper noetig */}
                    <span>
                      <DropdownMenuItem disabled className="text-destructive">
                        <Trash2 className="mr-2 size-4" />
                        Löschen
                      </DropdownMenuItem>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isEmptyNode
                      ? "Kann nicht gelöscht werden: fehlende Berechtigung."
                      : `Kann nicht gelöscht werden: hat noch ${
                          hasChildren && nodePositions.length > 0
                            ? "Unterknoten und Stellen"
                            : hasChildren
                              ? "Unterknoten"
                              : "Stellen"
                        }.`}
                  </TooltipContent>
                </Tooltip>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {hasContent && isExpanded && (
        <ul className="space-y-1">
          {/* Auf jeder Ebene zuerst die Stellen des Knotens, danach die Unterknoten */}
          {nodePositions.map((p) => (
            <li key={p.id} style={{ marginLeft: (depth + 1) * 20 }}>
              <button
                type="button"
                onClick={() => onSelectPosition(p)}
                className="ml-6 flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <Briefcase className="size-3.5 shrink-0" />
                <span>{p.name}</span>
              </button>
            </li>
          ))}
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              positions={positions}
              onSelectPosition={onSelectPosition}
              canWrite={canWrite}
              canDelete={canDelete}
              onCreateChild={onCreateChild}
              onAddPosition={onAddPosition}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  )
}
